import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import billingService from '../services/billingService';
import { apiErrorMessage } from '../utils/apiError';
import { formatDate } from '../utils/datetime';
import './BillingSchedule.css';

// A HOTEL'S BILLING SCHEDULE (V625 hotel_bill_to_periods) — who pays, by date: e.g. direct to the hotel from
// 1 Jan 2026, through its vendor from 1 Apr 2026. Each period starts on its date and runs until the next one starts,
// so the "to" is never entered and a schedule cannot have a gap or an overlap; the first period also covers anything
// before it. One editor for the vendor page's Chains and hotels and the hotel's own Billing tab, saved through the
// same PUT /billing/hotel/{id} (`periods`) — so the same checks apply: every payer must be the hotel's own or above
// it, and one whose period has not ended needs complete billing details.

export const today = () => new Date().toISOString().slice(0, 10);
export const nextDay = (day) => new Date(Date.parse(`${day}T00:00:00Z`) + 86400000).toISOString().slice(0, 10);
export const dayBefore = (day) => new Date(Date.parse(`${day}T00:00:00Z`) - 86400000).toISOString().slice(0, 10);

/** The period covering `day`: the last one started by then, else the first — none once billing has ended (V662). */
export const periodOn = (periods, day = today()) => {
    if (!periods || !periods.length) return null;
    const last = periods[periods.length - 1];
    if (last.to && day > last.to) return null;
    return periods.reduce((found, p) => (p.from <= day ? p : found), periods[0]);
};

/**
 * One line for a schedule: "Direct to hotel until 31 Mar 2026 · Through vendor from 1 Apr 2026". `labelOf(period)`
 * names each payer in the caller's terms. A schedule of one period is just its payer.
 */
export const scheduleText = (periods, labelOf) => {
    if (!periods || !periods.length) return '';
    if (periods.length === 1) return labelOf(periods[0]);
    return periods.map((p, i) => {
        if (i === 0) return `${labelOf(p)} until ${formatDate(p.to)}`;
        if (!p.to) return `${labelOf(p)} from ${formatDate(p.from)}`;
        return `${labelOf(p)} ${formatDate(p.from)} – ${formatDate(p.to)}`;
    }).join(' · ');
};

// A payer as one select value: HOTEL | CHAIN | PARTNER | VENDOR:<credential id>.
export const valueOf = (p) => (p.billTo === 'VENDOR' ? `VENDOR:${p.billToCredentialId || ''}` : p.billTo);
export const fromValue = (v) => (String(v).startsWith('VENDOR:')
    ? { billTo: 'VENDOR', billToCredentialId: parseInt(String(v).slice(7), 10) || null }
    : { billTo: v, billToCredentialId: null });

/** The payers a hotel may choose, from GET /billing/hotel/{id}'s `options`. */
export const payerChoices = (options = {}) => [
    { value: 'HOTEL', label: 'Direct to hotel' },
    ...(options.chain ? [{ value: 'CHAIN', label: `Chain — ${options.chain.name}` }] : []),
    ...(options.partner ? [{ value: 'PARTNER', label: `Partner — ${options.partner.name}` }] : []),
    ...(options.vendors || []).map(v => ({ value: `VENDOR:${v.id}`, label: `Vendor — ${v.name}` })),
];

/**
 * The editor, INLINE — opened in place under the hotel's row (STYLE.md: no modals). Loads the hotel's billing (schedule
 * and the payers it may choose), saves the whole schedule, and hands the response to `onSaved` (its billTo /
 * billToCredentialId are today's payer, `periods` the schedule). `defaultNew` is the payer a new period starts with —
 * the vendor page offers itself. `onClose` folds it away (Cancel, and after a save).
 */
export const BillingScheduleEditor = ({ hotelId, defaultNew = null, onClose, onSaved }) => {
    const [data, setData] = useState(null);
    const [rows, setRows] = useState([]);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let live = true;
        billingService.get('hotel', hotelId)
            .then(res => {
                if (!live) return;
                setData(res);
                const periods = res.periods && res.periods.length
                    ? res.periods
                    : [{ from: today(), billTo: res.billTo || 'HOTEL', billToCredentialId: res.billToCredentialId || null }];
                setRows(periods.map(p => ({ from: p.from, payer: valueOf(p) })));
            })
            .catch(e => { if (live) setError(apiErrorMessage(e, 'Could not load the billing schedule')); });
        return () => { live = false; };
    }, [hotelId]);

    const sorted = rows.map((r, i) => ({ ...r, i })).sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
    const toOf = (r) => {
        const k = sorted.findIndex(x => x.i === r.i);
        const next = sorted[k + 1];
        return next && next.from ? dayBefore(next.from) : null;
    };
    const set = (i, patch) => setRows(list => list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    const add = () => {
        const last = sorted[sorted.length - 1];
        const from = last && last.from >= today() ? nextDay(last.from) : today();
        setRows(list => [...list, { from, payer: defaultNew || (last ? last.payer : 'HOTEL') }]);
    };
    const remove = (i) => setRows(list => list.filter((_, j) => j !== i));

    const froms = rows.map(r => r.from);
    const problem = !rows.length ? 'Keep at least one period.'
        : rows.some(r => !r.from) ? 'Every period needs a start date.'
            : new Set(froms).size !== froms.length ? 'Two periods start on the same day.'
                : rows.some(r => r.payer === 'VENDOR:') ? 'Choose the vendor.' : null;

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const periods = rows.map(r => ({ from: r.from, ...fromValue(r.payer) }));
            const res = await billingService.save('hotel', hotelId, { details: {}, periods });
            onSaved && onSaved(res);
            onClose && onClose();
        } catch (e) {
            setError(apiErrorMessage(e, 'Could not save the billing schedule'));
        } finally {
            setSaving(false);
        }
    };

    // `data` is null until the hotel's billing has loaded — and a default parameter only replaces undefined, so
    // `payerChoices(null)` read null.chain and blanked the page on the first render (hotel Billing tab › Dates).
    const choices = payerChoices((data && data.options) || {});
    // Every button is type="button": the editor may sit inside a <form> (a hotel's Billing tab), which it must never submit.
    return (
        <div className="bsch-inline">
            <p className="bsch-hint">
                Each period runs from its start date until the next period starts; the last runs on with no end. The
                first period also covers every date before it. A change takes effect on its date — the hotel's
                invoices go to whoever pays on that day.
            </p>
            {!data && !error && <p className="bsch-hint">Loading…</p>}
            {data && (
                <table className="bsch-table">
                    <thead><tr><th>From</th><th>To</th><th>Billed to</th><th aria-label="Actions" /></tr></thead>
                    <tbody>
                        {sorted.map(r => {
                            const to = toOf(r);
                            const current = periodOn(sorted.filter(x => x.from), today());
                            return (
                                <tr key={r.i} className={current && current.i === r.i ? 'bsch-current' : undefined}>
                                    <td>
                                        <input type="date" className="acp-input bsch-date" value={r.from}
                                            onChange={e => set(r.i, { from: e.target.value })} aria-label="Period starts" />
                                    </td>
                                    <td className="bsch-to">{to ? formatDate(to) : 'onwards'}</td>
                                    <td>
                                        <select className="acp-select" value={r.payer} onChange={e => set(r.i, { payer: e.target.value })}
                                            aria-label="Billed to">
                                            {choices.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                            {!choices.some(c => c.value === r.payer) && (
                                                <option value={r.payer}>{r.payer.startsWith('VENDOR') ? 'A vendor no longer available' : r.payer}</option>
                                            )}
                                        </select>
                                        {current && current.i === r.i && <span className="bsch-now">today</span>}
                                    </td>
                                    <td>
                                        <button type="button" className="bsch-icon-btn" onClick={() => remove(r.i)}
                                            disabled={rows.length === 1} title="Remove this period" aria-label="Remove this period">
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            {data && (
                <button type="button" className="pp-criteria-add-btn bsch-add" onClick={add}>
                    <Plus size={14} /> Add a period
                </button>
            )}
            {problem && data && <p className="bsch-problem">{problem}</p>}
            {error && <div className="acp-error-banner">{error}</div>}
            <div className="bsch-actions">
                <button type="button" className="bsch-open-btn" onClick={onClose} disabled={saving}>Cancel</button>
                <button type="button" className="bsch-save-btn" onClick={save} disabled={!data || saving || !!problem}>
                    {saving ? 'Saving…' : 'Save schedule'}
                </button>
            </div>
        </div>
    );
};

/**
 * The schedule sorted, each period with its `to`: the day before the next one starts, or — on the last — its `end`
 * (V662 valid_to: billing stops after it; null runs on). Periods are entered by their start date, so they can never
 * overlap or leave a gap between them.
 */
export const withEnds = (periods = []) => {
    const sorted = [...periods].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
    return sorted.map((p, k) => ({
        ...p,
        to: sorted[k + 1] && sorted[k + 1].from ? dayBefore(sorted[k + 1].from) : (k === sorted.length - 1 && p.end) || null,
    }));
};

/** Why a schedule cannot be saved, or null. */
export const scheduleProblem = (periods = []) => {
    if (!periods.length) return 'Keep at least one billing period.';
    if (periods.some(p => !p.from)) return 'Every billing period needs a start date.';
    const froms = periods.map(p => p.from);
    if (new Set(froms).size !== froms.length) return 'Two billing periods start on the same day — dates cannot overlap.';
    if (periods.some(p => p.billTo === 'VENDOR' && !p.billToCredentialId)) return 'Choose the vendor.';
    if (withEnds(periods).some(p => p.to && p.to < p.from)) return 'A billing period cannot end before it starts.';
    return null;
};

/**
 * A schedule READ-ONLY — each window with its dates and who pays, today's marked. For whoever may see a hotel's billing but
 * not change it (a vendor on its own Chains and hotels): the editor's columns, without the controls. `labelOf(period)`
 * names each payer in the caller's terms, as scheduleText's.
 */
export const BillingScheduleView = ({ periods = [], labelOf }) => {
    const current = periodOn(periods);
    return (
        <table className="bsch-table bsch-view">
            <thead><tr><th>From</th><th>To</th><th>Billed to</th></tr></thead>
            <tbody>
                {periods.map((p, i) => (
                    <tr key={p.id || i} className={p === current ? 'bsch-current' : undefined}>
                        {/* The first window also covers everything before it (its stored 2000-01-01 is not a real start). */}
                        <td className="bsch-to">{i === 0 ? '—' : formatDate(p.from)}</td>
                        <td className="bsch-to">{p.to ? formatDate(p.to) : 'onwards'}</td>
                        <td>{labelOf(p)}{p === current && <span className="bsch-now">now</span>}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
